import { useQuery } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { agentsApi } from "@/api/agents";
import { supportTicketsApi } from "@/api/support-tickets";
import type { Company } from "@/api/companies";

/**
 * The pieces of a tenant row on the MSP side, shared by the Dashboard and
 * Tenants pages so both show a tenant the same way: logo, name, "3 Agents ·
 * 1 Open Ticket", and its real status.
 */

const STATUS_VARIANT: Record<string, "success" | "warning" | "danger"> = {
  active: "success",
  suspended: "warning",
  archived: "danger",
};

function plural(n: number, word: string): string {
  return `${n} ${word}${n !== 1 ? "s" : ""}`;
}

export function TenantLogo({ company }: { company: Company }) {
  if (company.logoUrl) {
    return (
      <img
        src={company.logoUrl}
        alt={company.name}
        className="h-10 w-10 rounded-lg object-contain"
      />
    );
  }
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
      <Building2 size={20} className="text-accent" />
    </div>
  );
}

/** The status as stored on the company, not a fixed "Active". */
export function TenantStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? "secondary"}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export function TenantCounts({ agents, openTickets }: { agents: number; openTickets: number }) {
  return (
    <p className="text-xs text-text-muted">
      {plural(agents, "Agent")} · {plural(openTickets, "Open Ticket")}
    </p>
  );
}

/** Every tenant's agents in one list. One fetch shared by both pages. */
export function useAllTenantAgents(companies: Company[]) {
  return useQuery({
    queryKey: ["admin", "all-agents", companies.map((c) => c.id).join(",")],
    queryFn: async () => {
      const results = await Promise.all(
        companies.map((c) => agentsApi.listByCompany(c.id))
      );
      return results.flat();
    },
    enabled: companies.length > 0,
  });
}

/**
 * Tickets still waiting on the platform team (open or in progress). Same query
 * and key as the Support Tickets page's default view, so it is one fetch.
 */
export function useOpenTickets() {
  const { data } = useQuery({
    queryKey: ["admin-support-tickets", ""],
    queryFn: () => supportTicketsApi.adminList(),
  });
  return (data?.tickets ?? []).filter(
    (t) => t.status === "open" || t.status === "in_progress",
  );
}
